import {
	DRAG_THRESHOLD_PX,
	MINUTES_PER_DAY,
	type Span,
	columnAt,
	movedSpan,
	pointerMinutes,
	resizedSpan,
	wallMinutes
} from './calendarDrag';

export type CalendarGeometry = { columns: DOMRect[]; gridTop: number; pxPerMinute: number };

export type DragGeometry = CalendarGeometry & { days: Date[]; dayStartHour: number };

export type DragMode = 'move' | 'resize';

export type DragPreview = { id: number } & Span;

export type LivePreview = DragPreview & { mode: DragMode };

export type DragPointer = Pick<PointerEvent, 'pointerId' | 'button' | 'pointerType' | 'clientX' | 'clientY'>;

type Options = {
	getGeometry: () => DragGeometry | null;
	timezone?: string;
	onPreview: (preview: LivePreview | null) => void;
	onDrop: (id: number, span: Span) => void;
};

type Session = {
	appointment: DragPreview;
	mode: DragMode;
	pointerId: number;
	x: number;
	y: number;
	grab: number;
	started: boolean;
	cancelled: boolean;
	previewing: boolean;
	span: Span;
	/** Where the meeting snaps to without moving: an off-grid meeting would otherwise move on a click's jitter. */
	unmoved: Span;
	captureElement: HTMLElement | null;
};

function dayCells(container: HTMLElement, hours: number, minutes: number): HTMLElement[] {
	const selector = `[data-index][data-hours="${hours}"][data-minutes="${minutes}"]`;
	return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter(
		cell => !cell.classList.contains('CalendarWeekday__hour')
	);
}

export function readCalendarGeometry(container: HTMLElement, dayStartHour: number): CalendarGeometry | null {
	const columns = dayCells(container, dayStartHour, 0)
		.sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
		.map(cell => cell.getBoundingClientRect());
	const halfHour = dayCells(container, dayStartHour, 30)[0];
	if (!columns.length || !halfHour) {
		return null;
	}
	const gridTop = columns[0].top;
	const pxPerMinute = (halfHour.getBoundingClientRect().top - gridTop) / 30;
	return pxPerMinute > 0 ? { columns, gridTop, pxPerMinute } : null;
}

function sameSpan(a: Span, b: Span): boolean {
	return a.date.getTime() === b.date.getTime() && a.endDate.getTime() === b.endDate.getTime();
}

export class CalendarDragController {
	private options: Options;
	private session: Session | null = null;
	private swallowClick = false;

	constructor(options: Options) {
		this.options = options;
	}

	start(appointment: DragPreview, pointer: DragPointer, mode: DragMode, captureElement: HTMLElement | null) {
		if (pointer.button !== 0 || pointer.pointerType !== 'mouse') {
			return;
		}
		const geometry = this.options.getGeometry();
		if (!geometry) {
			return;
		}
		if (this.session) {
			this.session.cancelled = true;
			this.finish(this.session);
		}
		this.swallowClick = false;

		const edge = this.edgeMinutes(appointment, mode);
		this.session = {
			appointment,
			mode,
			pointerId: pointer.pointerId,
			x: pointer.clientX,
			y: pointer.clientY,
			grab: this.pointerMinutes(geometry, pointer.clientY) - edge,
			started: false,
			cancelled: false,
			previewing: false,
			span: { date: appointment.date, endDate: appointment.endDate },
			unmoved: this.spanAt(appointment, mode, appointment.date, edge),
			captureElement
		};

		window.addEventListener('pointermove', this.onMove);
		window.addEventListener('pointerup', this.onUp);
		window.addEventListener('pointercancel', this.onCancel);
		window.addEventListener('keydown', this.onKey);
	}

	consumeClick(): boolean {
		const swallowed = this.swallowClick;
		this.swallowClick = false;
		return swallowed;
	}

	destroy() {
		if (this.session) {
			this.detach(this.session);
			this.session = null;
		}
	}

	private pointerMinutes(geometry: DragGeometry, clientY: number): number {
		return pointerMinutes(clientY, geometry.gridTop, geometry.pxPerMinute, geometry.dayStartHour);
	}

	private edgeMinutes(appointment: DragPreview, mode: DragMode): number {
		const start = wallMinutes(appointment.date, this.options.timezone);
		if (mode === 'move') {
			return start;
		}
		const end = wallMinutes(appointment.endDate, this.options.timezone);
		return end > start ? end : MINUTES_PER_DAY;
	}

	private spanAt(appointment: DragPreview, mode: DragMode, day: Date, minutes: number): Span {
		return mode === 'move'
			? movedSpan(appointment, day, minutes, this.options.timezone)
			: resizedSpan(appointment, minutes, this.options.timezone);
	}

	private clearPreview(current: Session) {
		if (current.previewing) {
			current.previewing = false;
			this.options.onPreview(null);
		}
	}

	private detach(current: Session) {
		window.removeEventListener('pointermove', this.onMove);
		window.removeEventListener('pointerup', this.onUp);
		window.removeEventListener('pointercancel', this.onCancel);
		window.removeEventListener('keydown', this.onKey);
		if (current.captureElement?.hasPointerCapture(current.pointerId)) {
			current.captureElement.releasePointerCapture(current.pointerId);
		}
	}

	private finish(current: Session) {
		this.detach(current);
		this.session = null;
		const dropped = current.started && !current.cancelled && !sameSpan(current.span, current.appointment);
		if (!dropped) {
			this.clearPreview(current);
		}
		if (!current.started && !current.cancelled) {
			return;
		}
		this.swallowClick = true;
		setTimeout(() => {
			this.swallowClick = false;
		});
		if (dropped) {
			this.options.onDrop(current.appointment.id, current.span);
		}
	}

	private onMove = (pointer: PointerEvent) => {
		const current = this.session;
		if (!current || pointer.pointerId !== current.pointerId) {
			return;
		}
		if (pointer.buttons === 0) {
			current.cancelled = true;
			this.finish(current);
			return;
		}
		if (current.cancelled) {
			return;
		}
		if (!current.started) {
			if (Math.hypot(pointer.clientX - current.x, pointer.clientY - current.y) < DRAG_THRESHOLD_PX) {
				return;
			}
			current.started = true;
			current.captureElement?.setPointerCapture(pointer.pointerId);
		}

		const geometry = this.options.getGeometry();
		if (!geometry) {
			return;
		}
		const minutes = this.pointerMinutes(geometry, pointer.clientY) - current.grab;
		const day =
			current.mode === 'move'
				? geometry.days[columnAt(geometry.columns, pointer.clientX)]
				: current.appointment.date;
		if (!day) {
			return;
		}
		const snapped = this.spanAt(current.appointment, current.mode, day, minutes);
		const span = sameSpan(snapped, current.unmoved)
			? { date: current.appointment.date, endDate: current.appointment.endDate }
			: snapped;

		if (!current.previewing || !sameSpan(span, current.span)) {
			current.span = span;
			current.previewing = true;
			this.options.onPreview({ id: current.appointment.id, ...span, mode: current.mode });
		}
	};

	private onUp = (pointer: PointerEvent) => {
		const current = this.session;
		if (current && pointer.pointerId === current.pointerId) {
			this.finish(current);
		}
	};

	private onCancel = (pointer: PointerEvent) => {
		const current = this.session;
		if (current && pointer.pointerId === current.pointerId) {
			current.cancelled = true;
			this.finish(current);
		}
	};

	private onKey = (key: KeyboardEvent) => {
		const current = this.session;
		if (key.key !== 'Escape' || !current) {
			return;
		}
		key.preventDefault();
		current.cancelled = true;
		current.span = { date: current.appointment.date, endDate: current.appointment.endDate };
		this.clearPreview(current);
	};
}
